"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  fadeUp,
  fadeScale,
  stagger,
  fastStagger,
  cardItem,
  viewportOnce,
} from "../../components/motionVariants";
import ProductHeroCta from "@/app/components/ProductHeroCta";
import {
  FileText,
  Factory,
  Package,
  Settings,
  Database,
  TrendingUp,
  Zap,
  Search,
  FileEdit,
  DoorOpen,
  Banknote,
  BarChart2,
  BookOpen,
  Scale,
  Landmark,
  Upload,
  Download,
  ClipboardList,
  Check,
} from "lucide-react";

// ─── Core Modules ─────────────────────────────────────────────────────
const modules = [
  {
    name: "Sales Management",
    Icon: FileText,
    desc: "Handle every step of your sales cycle - from customer quotes to recurring invoices and credit notes - with GST compliance built in at every document.",
    highlights: [
      "Customer → Quote → Sales Order → Delivery Challan → Invoice",
      "Recurring invoice automation",
      "Credit note & advance receipt",
      "E-Invoice (IRN) auto-generation",
      "Multi-currency invoice support",
    ],
  },
  {
    name: "Purchase Management",
    Icon: Factory,
    desc: "Complete purchase cycle from vendor to payment. Every bill links back to its Purchase Order and Goods Receipt (PO → GRPO → Bill), so you pay for what you ordered and received, and debit notes are tracked automatically.",
    highlights: [
      "Vendor Master with GSTIN validation",
      "Purchase Order → GRPO → Bill → Payment",
      "Debit note handling",
      "Recurring bill automation",
      "Expense categorisation for P&L",
    ],
  },
  {
    name: "Inventory Control",
    Icon: Package,
    desc: "Real-time stock tracking across multiple warehouses and branches. Stock moves automatically when Delivery Challans or Invoices are issued - no manual update needed.",
    highlights: [
      "Multi-warehouse & multi-branch stock",
      "Batch & expiry tracking",
      "Stock transfer between locations",
      "Inventory adjustments",
      "FIFO / Weighted Average valuation",
    ],
  },
  {
    name: "Production Tracking",
    Icon: Settings,
    desc: "For manufacturers and processors - define your Bill of Materials (recipes), create production orders, and track raw material consumption. Production costs flow automatically into COGS.",
    highlights: [
      "Bill of Materials (recipe) management",
      "Production order creation & submission",
      "Raw material consumption tracking",
      "Finished goods inventory update",
      "Production cost → COGS integration",
    ],
  },
  {
    name: "Masters Configuration",
    Icon: Database,
    desc: "All master data configured once, used everywhere. Items with HSN codes, manpower rates for production costing, multi-branch structure, warehouses, pricelists, and quality standards.",
    highlights: [
      "Item Master with HSN code & GST rate",
      "Manpower Master for production costing",
      "Multi-branch & warehouse setup",
      "Pricelist per customer segment",
      "Quality Master for production",
      "Division & process configuration",
    ],
  },
  {
    name: "Reports & Analytics",
    Icon: TrendingUp,
    desc: "12+ financial reports generated in seconds. General Ledger, P&L, Balance Sheet, and all ledger reports - filter by date, branch, or item. Export most reports to Excel or PDF.",
    highlights: [
      "General Ledger with drill-down",
      "Profit & Loss Statement",
      "Balance Sheet Summary",
      "Creditors & Debtors Aging",
      "Sales, Purchase & Bank Ledger",
      "Item-wise Sales & Ledger Analysis",
    ],
  },
  {
    name: "Automation & Accuracy",
    Icon: Zap,
    desc: "Recurring invoices, recurring bills, and recurring expenses run automatically on schedule. GSTIN validation, HSN code auto-fill, and duplicate detection on import cut down manual errors.",
    highlights: [
      "Recurring invoice / bill / expense automation",
      "GSTIN validation on vendor/customer entry",
      "Duplicate customer detection on import",
      "Multi-currency with exchange rates",
      "CRM sync - no double entry",
    ],
  },
];

// ─── Purchase Flow ─────────────────────────────────────────────────────
const purchaseSteps = [
  {
    step: "01",
    Icon: Search,
    title: "Vendor Master",
    desc: "Set up vendor with GSTIN validation, payment terms, and credit limit. For GST-registered vendors, ABSS auto-fills legal name and address from the GST portal.",
  },
  {
    step: "02",
    Icon: FileEdit,
    title: "Purchase Order",
    desc: "Create a PO with item quantities, rates, and expected delivery date. Track what's received and what's still pending against each PO.",
  },
  {
    step: "03",
    Icon: DoorOpen,
    title: "GRPO (Goods Receipt)",
    desc: "When goods arrive, raise a GRPO against the PO. Inventory is updated immediately. Partial receipts supported.",
  },
  {
    step: "04",
    Icon: FileText,
    title: "Bill (Purchase Invoice)",
    desc: "Create the bill straight from the GRPO - items, quantities, and rates carry over with no re-entry. GST input credit calculated automatically.",
  },
  {
    step: "05",
    Icon: Banknote,
    title: "Payment Made",
    desc: "Record payment against the bill. Multi-bank account support. Outstanding balance updated in real time.",
  },
  {
    step: "06",
    Icon: BarChart2,
    title: "Purchase Ledger",
    desc: "Full purchase history per vendor - every PO, GRPO, bill, and payment. Vendor aging and debit note tracking included.",
  },
];

// ─── Reports ──────────────────────────────────────────────────────────
const reports = [
  {
    name: "General Ledger",
    Icon: BookOpen,
    desc: "Full transaction history for any account, filterable by date, branch, and document type. Drill into any entry.",
  },
  {
    name: "Profit & Loss",
    Icon: BarChart2,
    desc: "Revenue vs. expenses breakdown for any period. Branch-wise and consolidated P&L. EBITDA calculation included.",
  },
  {
    name: "Balance Sheet",
    Icon: Scale,
    desc: "Assets vs. liabilities snapshot at any date. Includes current assets, fixed assets, long-term liabilities, and equity.",
  },
  {
    name: "Sales Ledger",
    Icon: FileText,
    desc: "All customer invoices and receipts with running balance. Filter by customer, date, or branch. Export to PDF.",
  },
  {
    name: "Purchase Ledger",
    Icon: Factory,
    desc: "All vendor bills and payments with aging. Outstanding payables and vendor-wise balances at a glance.",
  },
  {
    name: "Bank Ledger",
    Icon: Landmark,
    desc: "Complete bank account transaction history with running balance. Review entries and match payments manually.",
  },
  {
    name: "Creditors Ledger",
    Icon: Upload,
    desc: "Every vendor's balance with full transaction detail. Open any vendor to see bills, payments, and debit notes. Export to PDF or Excel.",
  },
  {
    name: "Debtors Ledger",
    Icon: Download,
    desc: "Every customer's balance with full transaction detail. Open any customer to see invoices, receipts, and credit notes. Export to PDF or Excel.",
  },
  {
    name: "Item Ledger",
    Icon: Package,
    desc: "Transaction history for any item - every sale, purchase, and stock transfer. Useful for product-level P&L.",
  },
  {
    name: "Customer Aging",
    Icon: TrendingUp,
    desc: "How long each customer's dues have been outstanding, in buckets you define - days, weeks, or months. Click any amount to see the invoices behind it.",
  },
  {
    name: "Vendor Aging",
    Icon: BarChart2,
    desc: "How long you've owed each vendor, in buckets you define - days, weeks, or months. Click any amount to see the bills behind it and plan payments.",
  },
  {
    name: "Item Module-wise Detail",
    Icon: Search,
    desc: "Which modules (Sales, Purchase, Production) an item appeared in, and when. Full cross-module item trace.",
  },
];

// ─── GST Compliance Detail ────────────────────────────────────────────
const complianceItems = [
  {
    Icon: Search,
    title: "GSTIN Validation at Entry",
    desc: "When you add a GST-registered vendor or customer, ABSS validates their GSTIN against the GST portal and auto-fills their legal name, address, and registration type.",
    badge: "Prevents input credit mismatches",
  },
  {
    Icon: FileText,
    title: "E-Invoice (IRN) Auto-Generation",
    desc: "For B2B invoices from eligible taxpayers, ABSS auto-generates the Invoice Reference Number (IRN) from the Invoice Registration Portal (IRP). QR code is embedded in the PDF.",
    badge: "IRP-compliant",
  },
  {
    Icon: Download,
    title: "E-Way Bill from Invoice or Challan",
    desc: "For goods above ₹50,000, generate the E-Way Bill straight from the Invoice or the Delivery Challan - add transporter, vehicle number, and distance, and it's attached to the document.",
    badge: "From Invoice or DC",
  },
  {
    Icon: ClipboardList,
    title: "HSN / SAC Code Auto-Fill",
    desc: "Items in your master carry HSN codes (goods) or SAC codes (services). These flow automatically into every invoice - no manual entry, no code lookup mistakes.",
    badge: "Item Master driven",
  },
  {
    Icon: Scale,
    title: "IGST / CGST / SGST Split",
    desc: "ABSS automatically applies the correct GST split based on the supply type - interstate (IGST) or intrastate (CGST + SGST). No manual tax calculation needed.",
    badge: "Location-aware tax split",
  },
];

// ─── Testimonials ─────────────────────────────────────────────────────
const testimonials = [
  {
    name: "Shubham Patel",
    role: "Director, Om Sara Pvt. Ltd.",
    city: "Ahmedabad, Gujarat",
    quote:
      "PCB manufacturing means tracking every component that goes into a board. With ABSS, our production orders consume raw materials automatically, stock stays accurate, and finished boards are ready to invoice with E-Invoice in the same system. Sales enquiries come in through CRM and flow straight into Books.",
    metric: "Production to E-Invoice, one system",
  },
  {
    name: "Nikunj Patel",
    role: "Founder, Om Power Electronics Pvt. Ltd.",
    city: "Ahmedabad, Gujarat",
    quote:
      "Making stabilizers means buying a lot of components from many vendors and storing them across different warehouses. ABSS Books gives us the full purchase cycle, from PO to goods receipt to bill, and we can see exactly what stock is in which warehouse at any time. With CRM on the sales side, everything stays in one system.",
    metric: "Multi-warehouse stock, always accurate",
  },
  {
    name: "Sejal Dudhat",
    role: "Director, Victor Sales Impex Pvt. Ltd.",
    city: "Ahmedabad, Gujarat",
    quote:
      "Our sales team makes a lot of calls every day, and before ABSS we had no clear picture of who was following up with whom. Now every call is logged against the lead in ABSS CRM, so we can see each salesperson's activity and how many of their leads turn into orders. The orders then flow straight into Books.",
    metric: "Every call tracked, every lead followed up",
  },
];

// Mock-dashboard status colors (neutral semantic palette, matches site toasts)
const STATUS_COLORS = {
  paid: "#16A34A",
  pending: "#D97706",
  overdue: "#DC2626",
};

export default function BooksPage() {
  const [activeModule, setActiveModule] = useState("Sales Management");
  const [activeReport, setActiveReport] = useState(null);

  const currentModule = modules.find((m) => m.name === activeModule);

  const [revenueOffset, setRevenueOffset] = useState(0);
  const [expensesOffset, setExpensesOffset] = useState(0);
  const [invoices, setInvoices] = useState([
    {
      id: 1,
      customer: "Mehta & Sons",
      amt: "₹84,500",
      status: "Paid",
      color: STATUS_COLORS.paid,
    },
    {
      id: 2,
      customer: "Kapoor Fabrics",
      amt: "₹1,24,000",
      status: "Pending",
      color: STATUS_COLORS.pending,
    },
    {
      id: 3,
      customer: "Sharma Traders",
      amt: "₹36,200",
      status: "Overdue",
      color: STATUS_COLORS.overdue,
    },
  ]);
  const [chartData, setChartData] = useState([
    30, 45, 35, 60, 49, 70, 65, 80, 75, 90,
  ]);

  useEffect(() => {
    const CUSTOMERS = [
      "Gupta Steel Works",
      "Verma Auto Parts",
      "Joshi Alloys",
      "Shah Polymers",
      "Nair Agri-Exports",
      "Reddy & Sons",
      "Patel Fabrics",
      "Mehta & Sons",
      "Sharma Traders",
    ];
    const AMOUNTS = [
      "₹45,000",
      "₹1,12,000",
      "₹2,30,000",
      "₹67,500",
      "₹1,45,000",
      "₹95,000",
      "₹36,200",
    ];
    const STATUSES = [
      { text: "Paid", color: STATUS_COLORS.paid },
      { text: "Pending", color: STATUS_COLORS.pending },
      { text: "Overdue", color: STATUS_COLORS.overdue },
    ];

    const interval = setInterval(() => {
      setRevenueOffset(
        (prev) => prev + Math.floor(Math.random() * 9000) - 2000,
      );
      setExpensesOffset(
        (prev) => prev + Math.floor(Math.random() * 5000) - 1000,
      );

      setInvoices((prev) => {
        const randomCustomer =
          CUSTOMERS[Math.floor(Math.random() * CUSTOMERS.length)];
        const randomAmt = AMOUNTS[Math.floor(Math.random() * AMOUNTS.length)];
        const randomStatus =
          STATUSES[Math.floor(Math.random() * STATUSES.length)];
        return [
          {
            id: Date.now(),
            customer: randomCustomer,
            amt: randomAmt,
            status: randomStatus.text,
            color: randomStatus.color,
          },
          ...prev.slice(0, 2),
        ];
      });

      setChartData((prev) => {
        const nextVal = Math.max(
          10,
          Math.min(
            100,
            prev[prev.length - 1] + (Math.floor(Math.random() * 31) - 15),
          ),
        );
        return [...prev.slice(1), nextVal];
      });
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden w-full">
      {/* ── HERO ───────────────────────────────────────────────────── */}
      <section className="relative pt-28 sm:pt-14 pb-16 sm:pb-20 bg-white border-b border-[#E5E5E5] overflow-hidden">
        {/* Ambient background - same treatment as the homepage hero */}
        <div className="absolute inset-0 z-0 select-none pointer-events-none">
          <div className="absolute top-[-25%] left-[-15%] w-[60%] h-[80%] rounded-full bg-gradient-to-br from-orange-200/40 to-amber-100/10 blur-[130px] animate-aurora-pulse" />
          <div className="absolute bottom-[-15%] right-[-10%] w-[55%] h-[75%] rounded-full bg-gradient-to-tr from-blue-200/30 to-indigo-100/10 blur-[130px] animate-aurora-pulse [animation-delay:3s]" />
          <div className="absolute inset-0 bg-grid-mesh opacity-80" />
        </div>

        <div className="relative z-10 max-w-[1160px] mx-auto px-5 sm:px-6 md:px-10 lg:px-12">
          <div className="grid md:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center min-w-0">
            <motion.div
              variants={stagger}
              initial="hidden"
              animate="show"
              className="min-w-0 w-full flex flex-col items-center text-center md:items-start md:text-left"
            >
              <motion.div variants={fadeUp} className="mb-5 flex justify-center md:justify-start">
                <span className="inline-flex items-center gap-1.5 border border-[#E5E5E5] rounded-full px-3.5 sm:px-4 py-1.5 text-[13px] text-[#262626] geist-semibold bg-white/80 backdrop-blur-sm shadow-sm">
                  <span className="w-2 h-2 rounded-full bg-[#DF5D01] inline-block animate-pulse" />
                  ABSS Books
                </span>
              </motion.div>
              <motion.h1
                variants={fadeUp}
                className="sentient-bold text-[28px] sm:text-[48px] lg:text-[54px] leading-[1.15] sm:leading-[1.12] text-[#171717] mb-5 sm:mb-6 tracking-tight"
              >
                GST-Native Accounting for Growing Indian SMEs
              </motion.h1>
              <motion.p
                variants={fadeUp}
                className="geist-medium text-[15px] sm:text-lg leading-relaxed text-[#525252] mb-7 sm:mb-8 max-w-xl"
              >
                ABSS Books handles your complete accounting cycle - sales,
                purchase, inventory, production, and 12+ reports. Every invoice
                is GST-compliant, and E-Invoice (IRN) and E-Way Bill are
                generated in one click.
              </motion.p>
              <motion.div
                variants={fadeUp}
                className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center md:justify-start gap-3 sm:gap-4 w-full max-w-[320px] sm:max-w-none"
              >
                <ProductHeroCta app="BOOKS" demoLabel="Book a Books Demo" />
                <a
                  href="#modules"
                  className="inline-flex items-center justify-center gap-2 h-[46px] sm:h-[48px] px-8 w-full sm:w-auto border border-[#E5E5E5] bg-white/80 backdrop-blur-sm text-[#262626] geist-semibold text-sm sm:text-base rounded-lg hover:bg-neutral-50 hover:text-black transition-all duration-300"
                >
                  Explore Modules ↓
                </a>
              </motion.div>

              {/* Trust pills */}
              <motion.div
                variants={fadeUp}
                className="flex flex-wrap justify-center md:justify-start gap-2 sm:gap-2.5 mt-7 sm:mt-8"
              >
                {[
                  "E-Invoice Ready",
                  "E-Way Bill",
                  "Multi-Warehouse",
                  "Production Module",
                ].map((label, i) => (
                  <span
                    key={i}
                    className="border border-[#E5E5E5] rounded-full px-3.5 py-1.5 text-xs sm:text-[13px] text-[#262626] geist-medium bg-white/70 backdrop-blur-sm shadow-sm hover:border-[#df5d01]/30 transition-all"
                  >
                    {label}
                  </span>
                ))}
              </motion.div>
            </motion.div>

            {/* Books Dashboard Preview */}
            <motion.div
              variants={fadeScale}
              initial="hidden"
              animate="show"
              className="bg-white border border-[#E5E5E5] rounded-xl sm:rounded-[16px] overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.08)] min-w-0 w-full"
            >
              {/* Mock Browser Header */}
              <div className="bg-[#FAFAFA] border-b border-[#E5E5E5] px-3 sm:px-4 py-3 sm:py-3.5 flex items-center gap-2 sm:gap-3 min-w-0">
                <div className="flex gap-1.5 shrink-0">
                  <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-[#E5E5E5] block" />
                  <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-[#E5E5E5] block" />
                  <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-[#E5E5E5] block" />
                </div>
                <div className="flex-1 min-w-0 max-w-[280px] bg-white border border-[#E5E5E5] rounded-full py-1 px-2.5 sm:px-4 flex items-center gap-1.5 text-[10px] sm:text-xs text-[#737373] font-mono">
                  <span className="text-[#A3A3A3] shrink-0">https://</span>
                  <span className="truncate">books.abss.ai/dashboard</span>
                </div>
                <div className="ml-auto hidden sm:flex items-center gap-1.5 bg-[#FFF7ED] text-[#DF5D01] px-2 sm:px-3 py-1 rounded-full text-[9px] sm:text-[10px] geist-bold uppercase tracking-wider border border-orange-100 shrink-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#DF5D01] inline-block animate-ping" />
                  Live Sync
                </div>
              </div>

              <div className="p-3.5 sm:p-6 space-y-3 sm:space-y-4">
                <div className="flex items-center justify-between mb-1 gap-2 min-w-0">
                  <span className="geist-bold text-sm text-[#171717] truncate">
                    ABSS Books - Financial Overview
                  </span>
                  <span className="text-[10px] text-[#A3A3A3] bg-[#F5F5F5] px-2 py-0.5 rounded geist-semibold uppercase tracking-wider shrink-0">
                    {(() => {
                      const date = new Date();
                      const months = [
                        "January",
                        "February",
                        "March",
                        "April",
                        "May",
                        "June",
                        "July",
                        "August",
                        "September",
                        "October",
                        "November",
                        "December",
                      ];
                      return `${months[date.getMonth()]} ${date.getFullYear()}`;
                    })()}
                  </span>
                </div>

                {/* KPIs */}
                <div className="grid grid-cols-2 gap-2 sm:gap-3">
                  {[
                    {
                      label: "Revenue",
                      baseVal: 1840000,
                      offset: revenueOffset,
                      color: "#171717",
                    },
                    {
                      label: "Expenses",
                      baseVal: 1120000,
                      offset: expensesOffset,
                      color: "#171717",
                    },
                    {
                      label: "Gross Profit",
                      baseVal: 720000,
                      offset: revenueOffset - expensesOffset,
                      color: "#DF5D01",
                    },
                    {
                      label: "Overdue Receivables",
                      baseVal: 210000,
                      offset: Math.floor(revenueOffset * 0.1),
                      color: "#DC2626",
                    },
                  ].map((kpi, i) => {
                    const finalVal = kpi.baseVal + kpi.offset;
                    const formatted =
                      "₹" + (finalVal / 100000).toFixed(2) + "L";
                    return (
                      <div
                        key={i}
                        className="bg-[#FAFAFA] border border-[#E5E5E5] p-2.5 sm:p-4 rounded-[10px] sm:rounded-[12px] transition-all duration-300 min-w-0"
                      >
                        <div
                          className="sentient-bold text-lg sm:text-2xl transition-colors duration-500 tracking-tight truncate"
                          style={{ color: kpi.color }}
                        >
                          {formatted}
                        </div>
                        <div className="geist-semibold text-[9px] sm:text-[10px] text-[#737373] mt-1 sm:mt-1.5 uppercase tracking-wider leading-tight">
                          {kpi.label}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Live Real-time Sparkline Graph */}
                <div className="bg-[#FAFAFA] border border-[#E5E5E5] p-4 rounded-[12px]">
                  <div className="flex justify-between items-center mb-3">
                    <span className="geist-bold text-[11px] text-[#737373] uppercase tracking-wider">
                      Real-time Billing Stream
                    </span>
                    <span className="text-[10px] text-[#DF5D01] geist-semibold flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#DF5D01] animate-ping" />
                      Active Stream
                    </span>
                  </div>
                  <div className="h-16 flex items-end">
                    <svg
                      className="w-full h-full text-[#DF5D01]"
                      viewBox="0 0 100 30"
                      preserveAspectRatio="none"
                    >
                      <defs>
                        <linearGradient
                          id="chartGrad"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="0%"
                            stopColor="#DF5D01"
                            stopOpacity="0.25"
                          />
                          <stop
                            offset="100%"
                            stopColor="#DF5D01"
                            stopOpacity="0"
                          />
                        </linearGradient>
                      </defs>
                      <path
                        d={`M 0 30 L ${chartData.map((val, idx) => `${idx * 11.1} ${30 - (val / 100) * 25}`).join(" L ")} L 100 30 Z`}
                        fill="url(#chartGrad)"
                        className="transition-all duration-500 ease-in-out"
                      />
                      <path
                        d={`M ${chartData.map((val, idx) => `${idx * 11.1} ${30 - (val / 100) * 25}`).join(" L ")}`}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="transition-all duration-500 ease-in-out"
                      />
                    </svg>
                  </div>
                </div>

                {/* Recent Invoices - Animated Ticker */}
                <div className="bg-[#FAFAFA] border border-[#E5E5E5] p-4 rounded-[12px]">
                  <div className="geist-bold text-[11px] text-[#737373] uppercase tracking-wider mb-3 flex items-center justify-between">
                    <span>Live Invoice Registry</span>
                    <span className="text-[9px] text-[#DF5D01] bg-[#FFF7ED] px-2 py-0.5 rounded-full border border-orange-100 uppercase geist-bold">
                      Auto-Syncing
                    </span>
                  </div>
                  <div className="space-y-2 h-[128px] overflow-hidden relative">
                    <AnimatePresence initial={false}>
                      {invoices.map((tx) => (
                        <motion.div
                          key={tx.id}
                          initial={{ opacity: 0, y: -20, height: 0 }}
                          animate={{ opacity: 1, y: 0, height: "auto" }}
                          exit={{ opacity: 0, y: 20, height: 0 }}
                          transition={{
                            type: "spring",
                            stiffness: 500,
                            damping: 30,
                          }}
                          className="flex items-center justify-between py-2.5 border-b border-[#E5E5E5]/60 last:border-0 overflow-hidden"
                        >
                          <span className="geist-semibold text-xs text-[#404040] leading-snug">
                            {tx.customer}
                          </span>
                          <div className="flex items-center gap-3">
                            <span className="geist-bold text-xs text-[#171717]">
                              {tx.amt}
                            </span>
                            <span
                              className="geist-bold text-[9px] px-2 py-0.5 rounded-full border"
                              style={{
                                color: tx.color,
                                borderColor: tx.color + "30",
                                backgroundColor: tx.color + "08",
                              }}
                            >
                              {tx.status}
                            </span>
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                </div>

                {/* GST Status */}
                <div className="bg-[#FAFAFA] border border-[#E5E5E5] p-4 rounded-[12px]">
                  <div className="geist-bold text-[11px] text-[#737373] uppercase tracking-wider mb-3 flex items-center justify-between">
                    <span>GST Compliance Status</span>
                    <span className="text-[9px] text-[#16A34A] bg-[#F0FDF4] border border-green-100 px-2 py-0.5 rounded-full uppercase geist-bold">
                      Matched
                    </span>
                  </div>
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#DF5D01] flex-shrink-0" />
                        <span className="geist-semibold text-xs text-[#404040] leading-snug">
                          E-Way Bill: {8 + Math.floor(revenueOffset / 200000)}{" "}
                          generated
                        </span>
                      </div>
                      <span className="text-[10px] text-[#A3A3A3] font-mono">
                        NIC Sync
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#DF5D01] flex-shrink-0 animate-pulse" />
                        <span className="geist-semibold text-xs text-[#404040] leading-snug">
                          E-Invoice: {12 + Math.floor(revenueOffset / 150000)}{" "}
                          IRNs generated
                        </span>
                      </div>
                      <span className="text-[10px] text-[#A3A3A3] font-mono">
                        IRP Sync
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── STATS ─────────────────────────────────────────────────── */}
      <section className="border-b border-[#E5E5E5] bg-white">
        <div className="max-w-[1160px] mx-auto px-5 sm:px-6 md:px-10 lg:px-12">
          <motion.div
            className="grid grid-cols-2 md:grid-cols-4 md:divide-x divide-[#E5E5E5]"
            variants={stagger}
            {...viewportOnce}
          >
            {[
              { num: "7", label: "Core Accounting Modules" },
              { num: "12+", label: "Financial Reports" },
              { num: "5", label: "GST Compliance Features" },
              { num: "100%", label: "Cloud - Any Device, Anywhere" },
            ].map((s, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                className="px-3 sm:px-6 md:px-8 py-6 sm:py-8 text-center"
              >
                <div className="sentient-medium text-3xl sm:text-4xl text-[#DF5D01] mb-1">
                  {s.num}
                </div>
                <div className="geist-semibold text-[10px] sm:text-xs uppercase tracking-wider text-[#A3A3A3] leading-snug">
                  {s.label}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── CORE MODULES ──────────────────────────────────────────── */}
      <section id="modules" className="py-16 sm:py-24 bg-white">
        <div className="max-w-[1160px] mx-auto px-5 sm:px-6 md:px-10 lg:px-12">
          <motion.div
            className="mb-10 sm:mb-14 text-center md:text-left flex flex-col items-center md:items-start"
            variants={stagger}
            {...viewportOnce}
          >
            <motion.p
              variants={fadeUp}
              className="geist-medium text-xs tracking-widest text-[#DF5D01] uppercase mb-3.5"
            >
              Core Modules
            </motion.p>
            <motion.h2
              variants={fadeUp}
              className="sentient-medium text-[24px] sm:text-[40px] leading-tight text-[#171717] mb-4 tracking-tight"
            >
              From Sales to Production - Everything in One Account
            </motion.h2>
            <motion.p
              variants={fadeUp}
              className="geist-regular text-base sm:text-lg text-[#525252] max-w-2xl leading-relaxed"
            >
              Seven interconnected modules. Data entered once flows everywhere.
              Click any module to see what it covers.
            </motion.p>
          </motion.div>

          {/* Module tab pills */}
          <motion.div
            variants={fadeUp}
            {...viewportOnce}
            className="flex flex-nowrap md:flex-wrap justify-center md:justify-start gap-2 mb-6 overflow-x-auto scrollbar-hide -mx-5 px-5 sm:-mx-6 sm:px-6 md:mx-0 md:px-0 pb-1"
          >
            {modules.map((mod) => (
              <button
                key={mod.name}
                onClick={() => setActiveModule(mod.name)}
                className={`flex items-center gap-2 px-4 h-10 rounded-lg border geist-semibold text-[13px] whitespace-nowrap transition-all duration-200 cursor-pointer flex-shrink-0 ${
                  activeModule === mod.name
                    ? "bg-[#171717] border-[#171717] text-white shadow-md"
                    : "bg-white border-[#E5E5E5] text-[#525252] hover:border-[#D4D4D4] hover:text-[#171717]"
                }`}
              >
                <mod.Icon className="w-4 h-4" />
                {mod.name}
              </button>
            ))}
          </motion.div>

          {/* Detail card */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeModule}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
              className="border border-[#E5E5E5] rounded-xl sm:rounded-[16px] bg-[#FAFAFA] p-4 sm:p-9 grid md:grid-cols-[1fr_1.15fr] gap-6 sm:gap-8 md:gap-12 items-start min-w-0"
            >
              {/* Left: title + description */}
              <div className="text-center md:text-left flex flex-col items-center md:items-start">
                <div className="flex items-center justify-center md:justify-start gap-3.5 mb-5">
                  {currentModule && (
                    <div className="w-11 h-11 rounded-[12px] bg-[#171717] text-white flex items-center justify-center flex-shrink-0">
                      <currentModule.Icon className="w-5 h-5" />
                    </div>
                  )}
                  <span className="sentient-medium text-[22px] sm:text-2xl text-[#171717] leading-tight">
                    {currentModule?.name}
                  </span>
                </div>
                <p className="geist-regular text-[#525252] text-[15px] leading-relaxed">
                  {currentModule?.desc}
                </p>
              </div>

              {/* Right: capabilities grid */}
              <div className="text-center md:text-left">
                <p className="geist-semibold text-[10px] tracking-widest text-[#DF5D01] uppercase mb-4">
                  Key Capabilities
                </p>
                <div className="grid sm:grid-cols-2 gap-2.5">
                  {currentModule?.highlights.map((h, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.04 }}
                      className="bg-white border border-[#E5E5E5] rounded-[10px] px-4 py-3 flex items-center gap-3"
                    >
                      <Check
                        className="w-4 h-4 text-[#DF5D01] flex-shrink-0"
                        strokeWidth={3}
                      />
                      <span className="geist-medium text-[13px] text-[#404040] leading-snug">
                        {h}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ── PURCHASE FLOW ─────────────────────────────────────────── */}
      <section className="py-16 sm:py-24 bg-[#FAFAFA] border-t border-[#E5E5E5]">
        <div className="max-w-[1160px] mx-auto px-5 sm:px-6 md:px-10 lg:px-12">
          <motion.div
            className="text-center mb-10 sm:mb-16"
            variants={stagger}
            {...viewportOnce}
          >
            <motion.p
              variants={fadeUp}
              className="geist-medium text-xs tracking-widest text-[#DF5D01] uppercase mb-3.5"
            >
              Purchase Cycle
            </motion.p>
            <motion.h2
              variants={fadeUp}
              className="sentient-medium text-[24px] sm:text-[40px] leading-tight text-[#171717] mb-4 tracking-tight"
            >
              Vendor to Payment - Every Step Linked
            </motion.h2>
            <motion.p
              variants={fadeUp}
              className="geist-regular text-base sm:text-lg text-[#525252] max-w-xl mx-auto leading-relaxed"
            >
              ABSS Books links every step of the purchase cycle automatically.
              Every bill traces back to its PO and goods receipt, so you pay for
              what you ordered and received.
            </motion.p>
          </motion.div>

          <div className="relative">
            {/* Connector line */}
            <div className="hidden lg:block absolute top-10 left-[calc(8.33%+20px)] right-[calc(8.33%+20px)] h-px bg-[#E5E5E5] z-0" />

            <motion.div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 relative z-10"
              variants={fastStagger}
              {...viewportOnce}
            >
              {purchaseSteps.map((step, i) => (
                <motion.div
                  key={i}
                  variants={cardItem}
                  className="bg-white border border-[#E5E5E5] hover:border-[#D4D4D4] rounded-[12px] p-4 sm:p-6 flex flex-col items-center text-center shadow-sm hover:shadow-md transition-all duration-300 min-w-0"
                >
                  <div className="w-9 h-9 bg-[#171717] text-white geist-bold text-xs rounded-full flex items-center justify-center mb-4 flex-shrink-0">
                    {step.step}
                  </div>
                  <div className="w-10 h-10 bg-[#F5F5F5] rounded-[10px] flex items-center justify-center mb-4 text-[#DF5D01]">
                    <step.Icon className="w-5 h-5" />
                  </div>
                  <h3 className="sentient-medium text-base text-[#171717] mb-2">
                    {step.title}
                  </h3>
                  <p className="geist-regular text-xs text-[#525252] leading-relaxed">
                    {step.desc}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── REPORTS ───────────────────────────────────────────────── */}
      <section className="py-16 sm:py-24 bg-white border-t border-[#E5E5E5]">
        <div className="max-w-[1160px] mx-auto px-5 sm:px-6 md:px-10 lg:px-12">
          <motion.div
            className="text-center mb-10 sm:mb-14"
            variants={stagger}
            {...viewportOnce}
          >
            <motion.p
              variants={fadeUp}
              className="geist-medium text-xs tracking-widest text-[#DF5D01] uppercase mb-3.5"
            >
              12+ Reports
            </motion.p>
            <motion.h2
              variants={fadeUp}
              className="sentient-medium text-[24px] sm:text-[40px] leading-tight text-[#171717] mb-4 tracking-tight"
            >
              Every Report Your CA or Banker Will Ever Ask For
            </motion.h2>
            <motion.p
              variants={fadeUp}
              className="geist-regular text-base sm:text-lg text-[#525252] max-w-2xl mx-auto leading-relaxed"
            >
              All reports are generated in real time, filterable by date range,
              branch, and item. No waiting. No exports to Excel to calculate
              manually.
            </motion.p>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
            variants={fastStagger}
            {...viewportOnce}
          >
            {reports.map((report, i) => (
              <motion.button
                key={i}
                variants={cardItem}
                onClick={() => setActiveReport(activeReport === i ? null : i)}
                className={`text-left p-4 sm:p-6 border rounded-[12px] transition-all duration-300 cursor-pointer min-w-0 ${
                  activeReport === i
                    ? "border-[#DF5D01]/40 bg-[#FFF7ED]/40"
                    : "border-[#E5E5E5] bg-white hover:bg-[#FAFAFA] hover:border-[#D4D4D4]"
                }`}
              >
                <div
                  className={`w-9 h-9 rounded-[10px] flex items-center justify-center mb-4 transition-all duration-300 ${
                    activeReport === i
                      ? "bg-[#171717] text-white"
                      : "bg-[#F5F5F5] text-[#525252]"
                  }`}
                >
                  <report.Icon className="w-4.5 h-4.5" />
                </div>
                <h3
                  className={`sentient-medium text-base mb-2 transition-colors text-[#171717]`}
                >
                  {report.name}
                </h3>
                <AnimatePresence>
                  {activeReport === i && (
                    <motion.p
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="geist-regular text-xs text-[#525252] leading-relaxed"
                    >
                      {report.desc}
                    </motion.p>
                  )}
                </AnimatePresence>
                {activeReport !== i && (
                  <p className="geist-semibold text-[10px] uppercase tracking-wider text-[#A3A3A3] mt-2 flex items-center gap-1">
                    <span>Click to expand</span>
                    <span className="text-[#DF5D01]">↓</span>
                  </p>
                )}
              </motion.button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── GST COMPLIANCE DEEP DIVE ───────────────────────────────── */}
      <section className="py-16 sm:py-24 bg-[#FAFAFA] border-t border-[#E5E5E5]">
        <div className="max-w-[1160px] mx-auto px-5 sm:px-6 md:px-10 lg:px-12">
          <motion.div
            className="text-center mb-10 sm:mb-14"
            variants={stagger}
            {...viewportOnce}
          >
            <motion.p
              variants={fadeUp}
              className="geist-medium text-xs tracking-widest text-[#DF5D01] uppercase mb-3.5"
            >
              India GST Compliance
            </motion.p>
            <motion.h2
              variants={fadeUp}
              className="sentient-medium text-[24px] sm:text-[40px] leading-tight text-[#171717] mb-4 tracking-tight"
            >
              Five Layers of GST Compliance - All Automatic
            </motion.h2>
            <motion.p
              variants={fadeUp}
              className="geist-regular text-base sm:text-lg text-[#525252] max-w-2xl mx-auto leading-relaxed"
            >
              GST in ABSS Books isn&apos;t a feature you switch on. It&apos;s
              wired into every vendor record, every invoice, every delivery, and
              every report.
            </motion.p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-4"
            variants={stagger}
            {...viewportOnce}
          >
            {complianceItems.map((item, i) => (
              <motion.div
                key={i}
                variants={cardItem}
                className="bg-white border border-[#E5E5E5] hover:border-[#D4D4D4] rounded-xl sm:rounded-[16px] p-5 sm:p-7 shadow-sm hover:shadow-md transition-all duration-300 min-w-0"
              >
                <div className="w-10 h-10 rounded-[10px] bg-[#F5F5F5] flex items-center justify-center mb-5 text-[#DF5D01]">
                  <item.Icon className="w-5 h-5" strokeWidth={2} />
                </div>
                <span className="inline-flex items-center text-[11px] geist-semibold text-[#DF5D01] bg-[#FFF7ED] border border-orange-100 rounded-full px-3 py-1 mb-4">
                  {item.badge}
                </span>
                <h3 className="sentient-medium text-xl text-[#171717] mb-3">
                  {item.title}
                </h3>
                <p className="geist-regular text-sm text-[#525252] leading-relaxed">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── TESTIMONIALS ──────────────────────────────────────────── */}
      <section className="py-16 sm:py-24 bg-white border-t border-[#E5E5E5]">
        <div className="max-w-[1160px] mx-auto px-5 sm:px-6 md:px-10 lg:px-12">
          <motion.div
            className="text-center mb-10 sm:mb-14"
            variants={stagger}
            {...viewportOnce}
          >
            <motion.p
              variants={fadeUp}
              className="geist-medium text-xs tracking-widest text-[#DF5D01] uppercase mb-3.5"
            >
              Customer Stories
            </motion.p>
            <motion.h2
              variants={fadeUp}
              className="sentient-medium text-[24px] sm:text-[40px] leading-tight text-[#171717] mb-4 tracking-tight"
            >
              What Indian SMEs Say About ABSS Books
            </motion.h2>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-3 gap-4"
            variants={stagger}
            {...viewportOnce}
          >
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                variants={cardItem}
                className="bg-white border border-[#E5E5E5] rounded-xl sm:rounded-[16px] p-5 sm:p-7 flex flex-col shadow-sm hover:shadow-md transition-all duration-300 min-w-0"
              >
                <p className="geist-regular text-[#525252] text-sm leading-relaxed mb-6 flex-1">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div className="border-t border-[#F5F5F5] pt-5 flex items-center gap-4">
                  <div className="w-11 h-11 rounded-full bg-[#F5F5F5] border border-[#E5E5E5] flex items-center justify-center text-sm geist-bold text-[#171717] flex-shrink-0">
                    {t.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")}
                  </div>
                  <div className="flex-1">
                    <div className="geist-semibold text-sm text-[#171717]">
                      {t.name}
                    </div>
                    <div className="geist-medium text-xs text-[#A3A3A3] mt-0.5">
                      {t.role}
                    </div>
                    <div className="geist-medium text-[11px] text-[#A3A3A3] mt-0.5">
                      {t.city}
                    </div>
                  </div>
                </div>
                <div className="mt-4 inline-flex items-center justify-center geist-semibold text-xs text-[#DF5D01] bg-[#FFF7ED] border border-orange-100 px-3 py-1 rounded-full self-start">
                  {t.metric}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
